import { Box } from '@ironhack/design-system2/components/box';
import { chakra } from '@ironhack/design-system2/components/chakra';
import { Flex, type FlexProps } from '@ironhack/design-system2/components/flex';
import { Text } from '@ironhack/design-system2/components/text';
import { useBreakpointValue } from '@ironhack/design-system2/hooks';
import { type BackgroundColor } from '@ironhack/design-system2/utils';
import { keyframes } from '@ironhack/design-system2/system';
import { isEmptyDocument } from 'datocms-structured-text-utils';
import { type ReactElement, type ToggleEventHandler } from 'react';
import { type StructuredTextGraphQlResponse } from 'react-datocms';
import { type Merge } from 'type-fest';

import { useIsClient } from '@/app/components/client/client-only';
import { StructuredContent } from '@/app/components/client/structured-content';
import { CmsImage } from '@/app/components/server/cms-image';
import {
  type DatoBackgroundColor,
  type DatoBlock,
  type DatoImage,
} from '@/lib/datocms/types';
import {
  ACTIVE_DISPLACEMENT,
  AccordionImageButton,
  BUTTON_RADIUS,
  CARD_TRANSITION,
} from '../accordion-image-button';

export type AccordionCard = DatoBlock<{
  description: StructuredTextGraphQlResponse;
  horizontalButtonImage: DatoImage;
  logoListBackground: DatoBackgroundColor | null;
  logos: DatoImage[];
  title: string;
  verticalButtonImage: DatoImage;
}>;

const TRANSITION_TIME = CARD_TRANSITION.split(' ')[0];

// Used to animate card opening on smaller screens
const heightAnimation = keyframes`
  from { height: 0 }
  to { height: auto }
`;

// Used to control height of content while cards open on bigger screens
// Height is 321px minus padding (16px x 2), gap (24px), and icon list (64px).
const heightClampAnimation = keyframes`
  0%, 80% { height: 201px }
  100% { height: auto }
`;

type Props = Merge<
  FlexProps,
  {
    data: AccordionCard;
    defaultLogoBackground?: BackgroundColor | `light-${BackgroundColor}`;
    name: string;
    open: boolean;
    onToggle: ToggleEventHandler<HTMLDetailsElement>;
  }
>;

export const AccordionCard = (props: Props): ReactElement => {
  const {
    data: {
      description,
      horizontalButtonImage,
      logoListBackground,
      logos,
      title,
      verticalButtonImage,
    },
    defaultLogoBackground = 'light',
    name,
    onToggle,
    open,
    ...flexProps
  } = props;

  const logoBackground =
    logoListBackground?.backgroundColor ?? defaultLogoBackground;

  const isHorizontalImage = useBreakpointValue({ base: true, md: false });
  const isClient = useIsClient();

  return (
    <Flex
      _active={{
        overflow: 'visible', // Allows pressed border to be visible
        // Displace card for pressed mode avoid showing white background
        marginBottom: [`-${ACTIVE_DISPLACEMENT}`, null, 0],
        marginRight: [null, null, `-${ACTIVE_DISPLACEMENT}`],
        transform: `translate(${ACTIVE_DISPLACEMENT}, ${ACTIVE_DISPLACEMENT})`,
      }}
      as="details"
      background="background.white"
      borderRadius={BUTTON_RADIUS} // Use button radius except when open
      direction={['column', null, 'row']}
      gap={0} // Set initial gap so it transitions
      name={name}
      onToggle={onToggle as unknown as ToggleEventHandler<HTMLDivElement>}
      open={open}
      overflow="hidden" // Prevents visible content while animating
      sx={{
        interpolateSize: 'allow-keywords', // Allows height auto animation
        '&[open]': {
          borderRadius: '3xl',
          flexGrow: 1,
          gap: 3, // When closed the gap is still visible
          // Reset card displacement (it happens on the button when open)
          marginBottom: 0,
          marginRight: 0,
          transform: 'none',
        },
        '&::details-content': {
          transition: [
            // Makes content visible when closing
            `content-visibility ${CARD_TRANSITION} allow-discrete`,
            null,
            'none',
          ],
        },
      }}
      transition={['flex-grow', 'gap']
        .map((prop) => `${prop} ${CARD_TRANSITION}`)
        .join(', ')}
      width={['100%', null, '82px']} // Prevents shrinking when other cards open
      {...flexProps}
    >
      <chakra.summary
        cursor="pointer"
        flexShrink={0} // Prevents shrinking when this card is open
        listStyleType="none"
        sx={{
          '[open] > &': {
            p: 2, // Apply padding here instead of details to animate separately
            pb: [0, null, 2],
            pr: [2, null, 0],
          },
        }}
        transition={`padding ${CARD_TRANSITION}`}
      >
        {(!isClient || isHorizontalImage) && (
          <AccordionImageButton
            counterName={name}
            display={[null, null, 'none']}
          >
            <CmsImage data={horizontalButtonImage.responsiveImage} />
          </AccordionImageButton>
        )}

        {(!isClient || !isHorizontalImage) && (
          <AccordionImageButton
            counterName={name}
            display={['none', null, 'block']}
          >
            <CmsImage data={verticalButtonImage.responsiveImage} />
          </AccordionImageButton>
        )}
      </chakra.summary>

      <Flex
        align="start"
        animation={[
          `${heightAnimation} ${TRANSITION_TIME} reverse`,
          null,
          '0s',
        ]}
        direction="column"
        gap={3}
        height={[null, null, '100%']}
        justify="space-between"
        key={`accordion-content-open-${String(open)}`} // Resets animations
        p={2}
        pl={[2, null, 0]}
        pt={[0, null, 2]}
        sx={{
          '[open] > &': {
            animation: [`${heightAnimation} ${TRANSITION_TIME}`, null, '0s'],
          },
        }}
      >
        {(title || !isEmptyDocument(description)) && (
          <Flex
            color="text.primary"
            direction="column"
            gap={2}
            overflowY={[null, null, 'hidden']}
            position={[null, null, 'relative']} // Anchors _after element
            sx={{
              '[open] &': {
                animation: [
                  null,
                  null,
                  `${heightClampAnimation} ${TRANSITION_TIME}`,
                ],
              },
            }}
          >
            {title && (
              <Text as="h2" textStyle="2xl">
                {title}
              </Text>
            )}
            {!isEmptyDocument(description) && (
              <StructuredContent data={description} textStyle="m" />
            )}
          </Flex>
        )}

        {logos.length > 0 && (
          <Flex
            background={`background.${logoBackground}`}
            borderRadius="3xl"
            gap={1}
            px={2}
            py={1}
          >
            {logos.map(
              (logo: DatoImage): ReactElement => (
                <Box
                  background="background.white"
                  borderRadius="md"
                  flexShrink={0} // Avoid shrinking while opening
                  key={logo.id}
                  overflow="hidden"
                  p={0.5}
                >
                  <CmsImage data={logo.responsiveImage} />
                </Box>
              )
            )}
          </Flex>
        )}
      </Flex>
    </Flex>
  );
};
